import { useCallback, useEffect, useRef, useState } from 'react';
import { ACHIEVEMENT_BY_CODE, THEMES, getGrade, getLevel, isHunt } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { useGame } from './useGame.js';
import { BoardView } from './BoardView.js';
import { BadgeBurst } from './BadgeBurst.js';
import { Praise } from './Praise.js';
import { ruleLine, verdictLine } from './verdict.js';
import { playClear } from '../sound.js';
import type { StringKey } from '../i18n/index.js';

export function GameScreen() {
  const { config, settings, themeId, t, num } = useSettings();
  const theme = THEMES[themeId];
  const grade = getGrade(settings.grade);
  const level = getLevel(settings.grade, settings.level);
  const game = useGame(config, settings.grade, level.index);
  const [queue, setQueue] = useState<string[]>([]);
  const seen = useRef(0);
  const hunt = isHunt(config);

  // Badges are celebrated one at a time, in the order they were earned; a new
  // board resets the watermark so the same badge is never replayed.
  useEffect(() => {
    // The tail is sliced HERE, not inside the updater: React defers the updater
    // to the next render, by which time the watermark has already advanced and
    // the slice would come back empty.
    const fresh = game.unlocked.slice(seen.current);
    seen.current = game.unlocked.length;
    if (fresh.length) setQueue((q) => [...q, ...fresh]);
  }, [game.unlocked]);

  useEffect(() => { if (game.status === 'cleared') playClear(); }, [game.status]);

  const dequeue = useCallback((code: string) => {
    setQueue((q) => q.filter((c) => c !== code));
  }, []);

  const ev = game.lastEvent;
  const equation = verdictLine(ev, { cfg: config, t, num, targetsLeft: game.targetsLeft });

  const banner =
    game.status === 'cleared' ? t('msg.cleared')
    : game.status === 'timeup' ? t('msg.timeUp')
    : !game.movesAvailable ? t('msg.noMoves')
    : '';

  const found = game.targetsDealt - game.targetsLeft;

  return (
    <div className="surface" data-art={theme.id} data-sel={theme.selectionStyle}>
      {/* Inert everywhere except the big-screen size, where the title and the
          HUD share one row so the board gets the height. */}
      <div className="game-top">
        <header className="game-head">
          <div className="game-title">
            <span className="game-grade">
              {t('msg.grade', { g: settings.grade })} · {t('msg.level', { n: level.index })} · {t(level.nameKey as StringKey)}
            </span>
            <h1 className="game-rule">{ruleLine(config, t)}</h1>
          </div>
        </header>

        <div className="hud">
          {config.timerSec !== null && (
            <Stat label={t('hud.time')} value={num(game.secondsLeft ?? 0)} warn={(game.secondsLeft ?? 0) <= 10} />
          )}
          <Stat label={t('hud.score')} value={num(game.stats.score)} />
          <Stat label={t('hud.combo')} value={`×${num(game.combo)}`} />
          {/* A hunt board counts what has been found; a pair board counts what
              is left, because those are the two numbers a child is watching. */}
          {hunt
            ? <Stat label={t('hud.found')} value={`${num(found)}/${num(game.targetsDealt)}`} />
            : <Stat label={t('hud.left')} value={num(game.tilesLeft)} />}
          <div className="meter" aria-hidden="true">
            <i style={{ width: `${Math.min(100, game.combo * 10)}%` }} />
          </div>
        </div>
      </div>

      <p className="equation" aria-live="polite" data-kind={ev?.kind ?? 'none'}>
        {banner || equation}
      </p>

      <div className="stage-wrap">
        <BoardView
          board={game.board}
          config={config}
          selected={game.selected}
          hintWhere={game.hintWhere}
          lastEvent={game.lastEvent}
          onTap={game.tap}
          disabled={game.status === 'cleared' || game.status === 'timeup'}
          seed={game.seed}
        />
        <Praise
          nonce={ev?.kind === 'match' ? ev.nonce : 0}
          level={config.celebration}
          combo={game.combo}
          t={t}
        />
      </div>

      <div className="acts">
        {/* A level with no allowance does not show a dead button. */}
        {config.adds > 0 && (
          <button className="gbtn" onClick={game.addRow} disabled={game.addsLeft === 0}>
            {t('btn.add')} <span className="cnt">{num(game.addsLeft)}</span>
          </button>
        )}
        <button className="gbtn" onClick={game.useHint} disabled={game.hintsLeft === 0}>
          {t('btn.hint')} <span className="cnt">{num(game.hintsLeft)}</span>
        </button>
        <button className="gbtn pri" onClick={() => game.restart()}>{t('btn.restart')}</button>
      </div>

      {game.unlocked.length > 0 && (
        <div className="shelf" aria-label={t('ach.unlocked')}>
          {game.unlocked.map((code) => {
            const ach = ACHIEVEMENT_BY_CODE.get(code);
            if (!ach) return null;
            return (
              <span className="shelf-badge" key={code} data-tier={ach.tier} title={t(ach.descKey as StringKey)}>
                <span className="shelf-medal" aria-hidden="true">{ach.icon}</span>
                <span className="shelf-name">{t(ach.nameKey as StringKey)}</span>
              </span>
            );
          })}
        </div>
      )}

      <BadgeBurst queue={queue} onShown={dequeue} />
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`stat${warn ? ' warn' : ''}`}>
      <span className="stat-k">{label}</span>
      <span className="stat-v">{value}</span>
    </div>
  );
}
